import { createContext, useContext, useEffect, useRef, useState } from "react";
import i18n from "@/lib/i18n";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);
const CACHE_KEY = "progres_user_v1";

function applyUserLanguage(user) {
  const lang = user?.language;
  if (lang && (lang === "pl" || lang === "en")) {
    const current = (i18n.resolvedLanguage || i18n.language || "").slice(0, 2);
    if (current !== lang) i18n.changeLanguage(lang);
  }
}

function readCachedUser() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // basic sanity: must be an object with id + email
    if (parsed && typeof parsed === "object" && parsed.id && parsed.email) return parsed;
  } catch (_e) {
    // corrupt cache — nuke
    localStorage.removeItem(CACHE_KEY);
  }
  return null;
}

function writeCachedUser(u) {
  try {
    if (u && typeof u === "object") localStorage.setItem(CACHE_KEY, JSON.stringify(u));
    else localStorage.removeItem(CACHE_KEY);
  } catch (_e) {
    // quota / privacy mode — ignore
  }
}

export function AuthProvider({ children }) {
  // Instant hydrate from localStorage (avoids "Ładowanie…" splash for returning users)
  const cached = typeof window !== "undefined" ? readCachedUser() : null;
  const [user, setUser] = useState(cached || null); // null = still checking, false = anon, object = user
  if (cached) applyUserLanguage(cached);
  const revalidating = useRef(false);

  const revalidate = async () => {
    if (revalidating.current) return;
    revalidating.current = true;
    try {
      const { data } = await api.get("/auth/me");
      applyUserLanguage(data);
      writeCachedUser(data);
      setUser(data);
    } catch (ex) {
      // If the cached session is invalid (401/403), drop it. Network errors: keep cached.
      const status = ex?.response?.status;
      if (status === 401 || status === 403) {
        writeCachedUser(null);
        setUser(false);
      } else if (!cached) {
        // No cache to fall back on → assume anonymous
        setUser(false);
      }
    } finally {
      revalidating.current = false;
    }
  };

  useEffect(() => {
    revalidate();
    // Silent revalidate when returning to the tab / PWA
    const onFocus = () => {
      if (document.visibilityState === "visible") revalidate();
    };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("progres_token", data.token);
    applyUserLanguage(data.user);
    writeCachedUser(data.user);
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, name, acceptTerms, attribution = {}) => {
    const { data } = await api.post("/auth/register", {
      email,
      password,
      name,
      accept_terms: !!acceptTerms,
      utm_source: attribution.utm_source || null,
      utm_medium: attribution.utm_medium || null,
      utm_campaign: attribution.utm_campaign || null,
      referrer: attribution.referrer || null,
    });
    // No auto-login: user must verify email first
    return data;
  };

  const verify = async (token) => {
    const { data } = await api.get(`/auth/verify?token=${encodeURIComponent(token)}`);
    if (data.token) localStorage.setItem("progres_token", data.token);
    applyUserLanguage(data.user);
    writeCachedUser(data.user);
    setUser(data.user);
    return data.user;
  };

  const refreshMe = async () => {
    try {
      const { data } = await api.get("/auth/me");
      applyUserLanguage(data);
      writeCachedUser(data);
      setUser(data);
      return data;
    } catch {
      return null;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_e) {
      // ignore
    }
    localStorage.removeItem("progres_token");
    writeCachedUser(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, verify, refreshMe, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { formatApiErrorDetail };
